'use client';

import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useProjectResponseModeContext } from '@/shared-survey';
import {
  stopAudioPlayback,
  WAVEFORM_PLAYER_CUSTOM_STOP_EVENT,
} from '@/shared-ui';
import { AUDIO_AMPLIFIER } from '@/ui';

import { RendererOption } from './project-response-renderer.type';
import { isVoiceComposerActive } from './project-response-voice-send.helpers';

export function useProjectResponseSend(type: RendererOption) {
  const {
    responseMode,
    toggleResponseMode,
    isListening,
    isStarting,
    audioLevelObservable,
    currentTranscript,
    eventsObservable,
    startDateOfTranscript,
    stopListening,
    manuallyEditTranscript,
  } = useProjectResponseModeContext();
  const { control, getValues, setValue } = useFormContext();
  const handleToggle = () => {
    // Stop any playing audio when toggling voice mode
    stopAudioPlayback();

    toggleResponseMode();
    const content = getValues('content');
    if (type === 'openEnded') {
      if (responseMode === 'voice') {
        setValue('content', currentTranscript);
      } else {
        manuallyEditTranscript(content);
      }
    }
  };

  useEffect(() => {
    if (currentTranscript) {
      window.dispatchEvent(new Event(WAVEFORM_PLAYER_CUSTOM_STOP_EVENT));
    }
  }, [currentTranscript]);

  useEffect(() => {
    const subscription = eventsObservable.subscribe((event) => {
      if (
        event.type === 'next_question' &&
        isVoiceComposerActive(responseMode, type)
      ) {
        setTimeout(() => setValue('content', ''), 100);
      }
    });
    return () => subscription.unsubscribe();
  }, [eventsObservable, setValue, responseMode, type]);

  const content = useWatch({ control, name: 'content' });

  return {
    audioLevelObservable,
    handleToggle,
    AUDIO_AMPLIFIER,
    isListening,
    isStarting,
    responseMode,
    currentTranscript,
    startDateOfTranscript,
    stopListening,
    content,
    manuallyEditTranscript,
  };
}
